import { getApiBaseUrl } from '@/config/runtimeUrls';
import { CELL_SCOPE_HEADER, readCellScope } from '@/services/cellScope';

const AUTH_TOKEN_KEYS = ['auth_token', 'auth_v2_accessToken', 'accessToken', 'authToken', 'token'];
const TENANT_LIST_CACHE_TTL_MS = 30_000;

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: any;
  fallback?: any;
  query?: Record<string, any>;
};

const buildQueryString = (query?: Record<string, any>): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.append(key, String(value));
  });
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

const readAccessToken = (): string => {
  if (typeof window === 'undefined') return '';
  for (const key of AUTH_TOKEN_KEYS) {
    const value = String(window.localStorage.getItem(key) || '').trim();
    if (value) return value;
  }
  return '';
};

const getHeaders = (): Record<string, string> => {
  const token = readAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    [CELL_SCOPE_HEADER]: readCellScope(),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const request = async <T = any>(path: string, options: RequestOptions = {}): Promise<T> => {
  const method = options.method || 'GET';
  const fallback = options.fallback as T;
  try {
    const response = await fetch(`${getApiBaseUrl()}${path}${buildQueryString(options.query)}`, {
      method,
      credentials: 'include',
      headers: getHeaders(),
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
    if (!response.ok) {
      return fallback;
    }
    const raw = await response.json().catch(() => ({}));
    if (raw && typeof raw === 'object' && 'data' in raw) {
      return raw.data as T;
    }
    return raw as T;
  } catch (_) {
    return fallback;
  }
};

const toCollection = <T = any>(payload: any, keys: string[] = []): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (!payload || typeof payload !== 'object') return [];
  for (const key of keys) {
    const value = (payload as Record<string, any>)[key];
    if (Array.isArray(value)) return value as T[];
  }
  return [];
};

const tenantListCache = new Map<string, { expiresAt: number; data: any[] }>();
const tenantListInflight = new Map<string, Promise<any[]>>();

const normalizeQueryKey = (params: Record<string, any> = {}): string =>
  JSON.stringify(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .sort(([a], [b]) => a.localeCompare(b))
  );

const invalidateTenantListCache = (): void => {
  tenantListCache.clear();
  tenantListInflight.clear();
};

const api = {
  // Dashboard
  getDashboardOverview: () => request('/superadmin/dashboard-overview', { fallback: {} }),
  getAnalytics: (range = '30d') =>
    request('/superadmin/analytics', { query: { range }, fallback: { revenueChart: [] } }),
  getSystemHealth: () => request('/superadmin/system-health', { fallback: {} }),
  getTenantHealthScores: (params: Record<string, any> = {}) =>
    request('/superadmin/analytics/health-scores', {
      query: params,
      fallback: { averageScore: 0, count: 0, items: [] },
    }),

  // Tenants
  listTenants: async (params: Record<string, any> = {}) => {
    const cacheKey = normalizeQueryKey(params);
    const now = Date.now();
    const cached = tenantListCache.get(cacheKey);
    if (cached && cached.expiresAt > now) {
      return cached.data;
    }

    const existingInflight = tenantListInflight.get(cacheKey);
    if (existingInflight) {
      return existingInflight;
    }

    const inflight = (async () => {
      const payload = await request('/superadmin/tenants', { query: params, fallback: [] });
      const data = toCollection(payload, ['tenants', 'items']);
      tenantListCache.set(cacheKey, {
        expiresAt: Date.now() + TENANT_LIST_CACHE_TTL_MS,
        data,
      });
      return data;
    })().finally(() => {
      tenantListInflight.delete(cacheKey);
    });

    tenantListInflight.set(cacheKey, inflight);
    return inflight;
  },
  getTenant: (tenantId: string) => request(`/superadmin/tenants/${tenantId}`, { fallback: null }),
  createTenant: (payload: any) =>
    request('/superadmin/tenants', { method: 'POST', body: payload, fallback: null }).then((result) => {
      invalidateTenantListCache();
      return result;
    }),
  updateTenant: (tenantId: string, payload: any) =>
    request(`/superadmin/tenants/${tenantId}`, { method: 'PUT', body: payload, fallback: null }).then((result) => {
      invalidateTenantListCache();
      return result;
    }),
  updateTenantStatus: (tenantId: string, status: string, reason?: string) =>
    request(`/superadmin/tenants/${tenantId}/status`, {
      method: 'PUT',
      body: { status, reason },
      fallback: null,
    }).then((result) => {
      invalidateTenantListCache();
      return result;
    }),
  deleteTenant: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}`, { method: 'DELETE', fallback: null }).then((result) => {
      invalidateTenantListCache();
      return result;
    }),
  resetTenantAdminPassword: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}/reset-admin-password`, {
      method: 'POST',
      fallback: {},
    }),
  impersonateTenant: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}/impersonate`, { method: 'POST', fallback: {} }),
  refreshTenantCache: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}/refresh-cache`, { method: 'POST', fallback: {} }),
  cleanupTenantOrphans: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}/cleanup-orphans`, { method: 'POST', fallback: {} }),
  exportTenantData: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}/export`, { method: 'POST', fallback: {} }),
  getTenantAnalytics: (tenantId: string, range = '30d') =>
    request(`/superadmin/tenants/${tenantId}/analytics`, {
      query: { range },
      fallback: { activities: {}, aiUsage: {}, documents: {} },
    }),
  getTenantBilling: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}/billing`, { fallback: {} }),
  getTenantHealth: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}/health`, { fallback: {} }),
  getTenantFeatures: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}/features`, { fallback: {} }),
  getTenantUsers: (tenantId: string, params: Record<string, any> = {}) =>
    request(`/superadmin/tenants/${tenantId}/users`, { query: params, fallback: [] }),
  getTenantSecurity: (tenantId: string) =>
    request(`/superadmin/tenants/${tenantId}/security`, { fallback: {} }),
  getTenantAuditLogs: (tenantId: string, params: Record<string, any> = {}) =>
    request(`/superadmin/tenants/${tenantId}/audit-logs`, {
      query: params,
      fallback: { items: [], pagination: { page: 1, limit: 25, total: 0 } },
    }),

  // Billing
  listInvoices: async () => {
    const payload = await request('/superadmin/billing/invoices', { fallback: [] });
    return toCollection(payload, ['items', 'invoices']);
  },
  createInvoice: (payload: any) =>
    request('/superadmin/billing/invoices', { method: 'POST', body: payload, fallback: null }),

  // Credits
  getCreditWallets: async () => {
    const payload = await request('/superadmin/credits/wallets', { fallback: [] });
    return toCollection(payload, ['items', 'wallets']);
  },
  getCreditWallet: (tenantId: string) =>
    request(`/superadmin/credits/wallets/${tenantId}`, { fallback: { wallet: null, ledger: { items: [] } } }),
  getCreditForecast: (tenantId: string) =>
    request(`/superadmin/credits/forecast/${tenantId}`, { fallback: { burnRate: {}, forecast: {} } }),
  grantCredits: (payload: any) =>
    request('/superadmin/credits/grant', { method: 'POST', body: payload, fallback: null }),
  debitCredits: (payload: any) =>
    request('/superadmin/credits/debit', { method: 'POST', body: payload, fallback: null }),

  // Usage analytics
  getUsageSummary: (tenantId: string, days = 7) =>
    request(`/superadmin/usage/summary/${tenantId}`, {
      query: { days },
      fallback: { byFeature: {}, meters: [] },
    }),
  getUsageEvents: (tenantId: string, params: Record<string, any> = {}) =>
    request(`/superadmin/usage/events/${tenantId}`, {
      query: params,
      fallback: { items: [] },
    }),

  // Audit
  getPlatformAuditLogs: (params: Record<string, any> = {}) =>
    request('/superadmin/audit-logs', {
      query: params,
      fallback: { items: [], pagination: { page: 1, limit: 25, total: 0 } },
    }),

  // Workers and queues
  getQueueStatus: () => request('/superadmin/ops/queues', { fallback: {} }),
  getJobMetrics: () => request('/superadmin/ops/metrics/jobs', { fallback: { metrics: {} } }),
  getCircuitBreakers: () =>
    request('/superadmin/ops/circuit-breakers', { fallback: { feature: {}, provider: {} } }),
  getWorkerHealthSnapshot: (includeTimeline = false) =>
    request('/superadmin/health/workers', {
      query: { includeTimeline },
      fallback: { workerStatus: {} },
    }),
  getWorkerHealth: () => request('/superadmin/system/workers/health', { fallback: { workers: [], queues: {} } }),

  // System administration
  listSystemFeatureFlags: () => request('/superadmin/system/feature-flags', { fallback: [] }),
  listMaintenance: () => request('/superadmin/system/maintenance', { fallback: [] }),
  listBackups: () => request('/superadmin/system/backups', { fallback: [] }),
  listApiLimits: () => request('/superadmin/system/api-limits', { fallback: [] }),
  listIpAllowlist: () => request('/superadmin/system/ip-allowlist', { fallback: [] }),
  listWafRules: () => request('/superadmin/system/waf-rules', { fallback: [] }),
  getEnvStatus: () => request('/superadmin/system/env-status', { fallback: {} }),
  getKeepWarm: () => request('/superadmin/keep-warm', { fallback: { enabled: false } }),
  setKeepWarm: (enabled: boolean) =>
    request('/superadmin/keep-warm', { method: 'POST', body: { enabled }, fallback: {} }),
  updateSystemFeatureFlag: (payload: any) =>
    request('/superadmin/system/feature-flags', { method: 'PUT', body: payload, fallback: {} }),
  createMaintenance: (payload: any) =>
    request('/superadmin/system/maintenance', { method: 'POST', body: payload, fallback: {} }),
  createBackup: (payload: any) =>
    request('/superadmin/system/backups', { method: 'POST', body: payload, fallback: {} }),
  updateApiLimit: (payload: any) =>
    request('/superadmin/system/api-limits', { method: 'PUT', body: payload, fallback: {} }),
  updateIpAllowlist: (payload: any) =>
    request('/superadmin/system/ip-allowlist', { method: 'PUT', body: payload, fallback: {} }),
  updateWafRule: (payload: any) =>
    request('/superadmin/system/waf-rules', { method: 'PUT', body: payload, fallback: {} }),

  // Additional panels used by dashboard tabs
  listIncidents: (params: Record<string, any> = {}) =>
    request('/superadmin/incidents', { query: params, fallback: { items: [] } }),
  updateIncident: (incidentId: string, payload: any) =>
    request(`/superadmin/incidents/${incidentId}`, { method: 'PUT', body: payload, fallback: {} }),
  createIncident: (payload: any) =>
    request('/superadmin/incidents', { method: 'POST', body: payload, fallback: {} }),
  getServiceHealth: () => request('/superadmin/service-health', { fallback: { services: [] } }),
  getDiagnostics: () => request('/superadmin/diagnostics', { fallback: { system: {}, performance: {}, memory: {} } }),
  getThreatMonitor: (params: Record<string, any> = {}) =>
    request('/superadmin/security/threat-monitor', { query: params, fallback: { summary: {}, events: [] } }),
  getFeatureUsage: (params: Record<string, any> = {}) =>
    request('/superadmin/feature-usage', { query: params, fallback: { rows: [] } }),
  getApiExplorer: (params: Record<string, any> = {}) =>
    request('/superadmin/api-explorer', { query: params, fallback: [] }),
  listRequests: (params: Record<string, any> = {}) =>
    request('/superadmin/request-inspector', { query: params, fallback: { items: [] } }),
  getRequestDetail: (requestId: string) =>
    request(`/superadmin/request-inspector/${requestId}`, { fallback: null }),
  listOrgUnits: () => request('/superadmin/org/units', { fallback: [] }),
  listOrgMembers: () => request('/superadmin/org/members', { fallback: [] }),
  listOnCallRotas: () => request('/superadmin/org/on-call-rotas', { fallback: [] }),
  listOrgRoles: () => request('/superadmin/org/roles', { fallback: [] }),
  createOrgUnit: (payload: any) =>
    request('/superadmin/org/units', { method: 'POST', body: payload, fallback: {} }),
  createOrgMember: (payload: any) =>
    request('/superadmin/org/members', { method: 'POST', body: payload, fallback: {} }),
  createOnCallRota: (payload: any) =>
    request('/superadmin/org/on-call-rotas', { method: 'POST', body: payload, fallback: {} }),
  createOrgRole: (payload: any) =>
    request('/superadmin/org/roles', { method: 'POST', body: payload, fallback: {} }),
  listPlans: () => request('/superadmin/plans', { fallback: [] }),
  createPlan: (payload: any) => request('/superadmin/plans', { method: 'POST', body: payload, fallback: {} }),
  updatePlan: (planId: string, payload: any) =>
    request(`/superadmin/plans/${planId}`, { method: 'PUT', body: payload, fallback: {} }),
  deletePlan: (planId: string) =>
    request(`/superadmin/plans/${planId}`, { method: 'DELETE', fallback: {} }),
  getAdminRoles: () => request('/superadmin/roles', { fallback: [] }),
  search: (category: string, query: string) =>
    request(`/superadmin/search/${category}`, { query: { q: query }, fallback: { items: [] } }),
};

const fallbackForUnknown = (methodName: string): any => {
  if (methodName.startsWith('list')) return [];
  if (methodName.startsWith('get')) return {};
  return null;
};

const SuperAdminAPI = new Proxy(api as Record<string, any>, {
  get(target, prop) {
    if (typeof prop !== 'string') return target[prop as any];
    if (prop in target) return target[prop];
    return async () => fallbackForUnknown(prop);
  },
});

export default SuperAdminAPI;

